import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Plus, Trash2, Edit2, Save, X, Upload, 
  Image, Video, Eye, Heart, Crown, Loader2, Building2,
  MapPin, Phone, ExternalLink, ToggleLeft, ToggleRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: "fashion", label: "패션" },
  { value: "hair", label: "헤어" },
  { value: "makeup", label: "메이크업" },
  { value: "procedure", label: "시술&성형" },
  { value: "profile", label: "프로필 사진" },
];

const contentTypes = [
  { value: "video", label: "영상", icon: Video },
  { value: "image", label: "이미지", icon: Image },
];

export default function AdminImageConsultingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, token, user, loading } = useAuth();
  
  const [activeTab, setActiveTab] = useState("contents"); // "contents" or "sponsors"
  const [contents, setContents] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showSponsorForm, setShowSponsorForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingSponsorImage, setUploadingSponsorImage] = useState(false);
  
  // Form state for content
  const [formData, setFormData] = useState({
    title: "",
    category: "fashion",
    description: "",
    content_type: "video",
    media_url: "",
    thumbnail_url: "",
    credits: 1,
    tags: "",
    is_premium: false,
  });

  // Form state for sponsor
  const [sponsorFormData, setSponsorFormData] = useState({
    title: "",
    category: "fashion",
    description: "",
    image_url: "",
    link_url: "",
    contact: "",
    location: "",
    is_active: true,
  });

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated || user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchContents();
    fetchSponsors();
  }, [isAuthenticated, user, loading]);

  const fetchContents = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/image-consulting/contents`);
      const data = await response.json();
      setContents(data.contents || []);
    } catch (error) {
      console.error("Error fetching contents:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSponsors = async () => {
    try {
      const response = await fetch(`${API_URL}/api/image-consulting/sponsors`);
      const data = await response.json();
      setSponsors(data.sponsors || []);
    } catch (error) {
      console.error("Error fetching sponsors:", error);
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    const isMedia = type === "media";
    if (isMedia) setUploadingMedia(true);
    else setUploadingThumbnail(true);

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    formDataUpload.append("media_type", file.type.startsWith("video") ? "video" : "image");

    try {
      const response = await fetch(`${API_URL}/api/admin/image-consulting/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();
      
      if (isMedia) {
        setFormData(prev => ({ ...prev, media_url: data.url }));
      } else {
        setFormData(prev => ({ ...prev, thumbnail_url: data.url }));
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("파일 업로드에 실패했습니다.");
    } finally {
      if (isMedia) setUploadingMedia(false);
      else setUploadingThumbnail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const contentData = {
      ...formData,
      tags: formData.tags.split(",").map(t => t.trim()).filter(t => t),
    };

    try {
      const url = editingContent
        ? `${API_URL}/api/admin/image-consulting/contents/${editingContent.id}`
        : `${API_URL}/api/admin/image-consulting/contents`;
      
      const response = await fetch(url, {
        method: editingContent ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(contentData),
      });

      if (!response.ok) throw new Error("Failed to save");

      await fetchContents();
      resetForm();
    } catch (error) {
      console.error("Save error:", error);
      alert("저장에 실패했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      const response = await fetch(
        `${API_URL}/api/admin/image-consulting/contents/${contentId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Delete failed");

      await fetchContents();
    } catch (error) {
      console.error("Delete error:", error);
      alert("삭제에 실패했습니다.");
    }
  };

  const handleEdit = (content) => {
    setEditingContent(content);
    setFormData({
      title: content.title,
      category: content.category,
      description: content.description,
      content_type: content.content_type,
      media_url: content.media_url || "",
      thumbnail_url: content.thumbnail_url || "",
      credits: content.credits || 1,
      tags: content.tags?.join(", ") || "",
      is_premium: content.is_premium || false,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingContent(null);
    setFormData({
      title: "",
      category: "fashion",
      description: "",
      content_type: "video",
      media_url: "",
      thumbnail_url: "",
      credits: 1,
      tags: "",
      is_premium: false,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-burgundy-500" />
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== "admin") {
    return null;
  }

  // Sponsor handlers
  const handleSponsorSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const url = editingSponsor
        ? `${API_URL}/api/admin/image-consulting/sponsors/${editingSponsor.id}`
        : `${API_URL}/api/admin/image-consulting/sponsors`;
      
      const response = await fetch(url, {
        method: editingSponsor ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(sponsorFormData),
      });

      if (!response.ok) throw new Error("Failed to save");

      await fetchSponsors();
      resetSponsorForm();
    } catch (error) {
      console.error("Save error:", error);
      alert("저장에 실패했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSponsor = async (sponsorId) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      const response = await fetch(
        `${API_URL}/api/admin/image-consulting/sponsors/${sponsorId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Delete failed");

      await fetchSponsors();
    } catch (error) {
      console.error("Delete error:", error);
      alert("삭제에 실패했습니다.");
    }
  };

  const handleEditSponsor = (sponsor) => {
    setEditingSponsor(sponsor);
    setSponsorFormData({
      title: sponsor.title,
      category: sponsor.category,
      description: sponsor.description,
      image_url: sponsor.image_url || "",
      link_url: sponsor.link_url || "",
      contact: sponsor.contact || "",
      location: sponsor.location || "",
      is_active: sponsor.is_active !== false,
    });
    setShowSponsorForm(true);
  };

  const resetSponsorForm = () => {
    setShowSponsorForm(false);
    setEditingSponsor(null);
    setSponsorFormData({
      title: "",
      category: "fashion",
      description: "",
      image_url: "",
      link_url: "",
      contact: "",
      location: "",
      is_active: true,
    });
  };

  const handleSponsorImageUpload = async (file) => {
    if (!file) return;
    setUploadingSponsorImage(true);

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    formDataUpload.append("media_type", "image");

    try {
      const response = await fetch(`${API_URL}/api/admin/image-consulting/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();
      setSponsorFormData(prev => ({ ...prev, image_url: data.url }));
    } catch (error) {
      console.error("Upload error:", error);
      alert("파일 업로드에 실패했습니다.");
    } finally {
      setUploadingSponsorImage(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 pt-20" data-testid="admin-image-consulting-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-stone-500 hover:text-stone-700 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              뒤로가기
            </button>
            <h1 className="text-3xl font-bold text-stone-800">이미지 컨설팅 관리</h1>
            <p className="text-stone-500 mt-1">콘텐츠와 추천업체를 관리하세요</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab("contents")}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === "contents"
                ? "bg-burgundy-500 text-white shadow-lg"
                : "bg-white text-stone-600 hover:bg-stone-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              콘텐츠 관리
            </div>
          </button>
          <button
            onClick={() => setActiveTab("sponsors")}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === "sponsors"
                ? "bg-burgundy-500 text-white shadow-lg"
                : "bg-white text-stone-600 hover:bg-stone-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              추천업체 관리
            </div>
          </button>
        </div>

        {/* Tab Header with Add Button */}
        <div className="flex justify-end mb-4">
          {activeTab === "contents" ? (
            <Button
              onClick={() => setShowForm(true)}
              className="bg-burgundy-500 hover:bg-burgundy-600 text-white"
            >
              <Plus className="w-5 h-5 mr-2" />
              새 콘텐츠 추가
            </Button>
          ) : (
            <Button
              onClick={() => setShowSponsorForm(true)}
              className="bg-gold-500 hover:bg-gold-600 text-stone-900"
            >
              <Plus className="w-5 h-5 mr-2" />
              새 추천업체 추가
            </Button>
          )}
        </div>

        {/* Contents Tab */}
        {activeTab === "contents" && (
          <>
            {/* Content List */}
            {isLoading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 text-burgundy-500 animate-spin" />
              </div>
            ) : contents.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl">
                <Image className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500 text-lg">등록된 콘텐츠가 없습니다.</p>
                <Button
                  onClick={() => setShowForm(true)}
                  className="mt-4 bg-burgundy-500 hover:bg-burgundy-600"
                >
              첫 콘텐츠 추가하기
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-stone-600">썸네일</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-stone-600">제목</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-stone-600">카테고리</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-stone-600">유형</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-stone-600">통계</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-stone-600">프리미엄</th>
                  <th className="text-right py-4 px-6 text-sm font-semibold text-stone-600">관리</th>
                </tr>
              </thead>
              <tbody>
                {contents.map((content) => (
                  <tr key={content.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="py-4 px-6">
                      <div className="w-20 h-12 bg-stone-200 rounded-lg overflow-hidden">
                        {content.thumbnail_url ? (
                          <img
                            src={content.thumbnail_url.startsWith("http") ? content.thumbnail_url : `${API_URL}${content.thumbnail_url}`}
                            alt={content.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {content.content_type === "video" ? (
                              <Video className="w-5 h-5 text-stone-400" />
                            ) : (
                              <Image className="w-5 h-5 text-stone-400" />
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <p className="font-medium text-stone-800 line-clamp-1">{content.title}</p>
                    </td>
                    <td className="py-4 px-6">
                      <span className="px-2 py-1 bg-stone-100 text-stone-600 text-xs rounded-full">
                        {categories.find(c => c.value === content.category)?.label || content.category}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        content.content_type === "video" 
                          ? "bg-burgundy-100 text-burgundy-600" 
                          : "bg-gold-100 text-gold-700"
                      }`}>
                        {content.content_type === "video" ? "영상" : "이미지"}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3 text-stone-500 text-sm">
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {content.views || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Heart className="w-4 h-4" />
                          {content.likes || 0}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      {content.is_premium && (
                        <Crown className="w-5 h-5 text-gold-500" />
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(content)}
                          className="p-2 text-stone-400 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(content.id)}
                          className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </>
        )}

        {/* Sponsors Tab */}
        {activeTab === "sponsors" && (
          <>
            {sponsors.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl">
                <Building2 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500 text-lg">등록된 추천업체가 없습니다.</p>
                <Button
                  onClick={() => setShowSponsorForm(true)}
                  className="mt-4 bg-gold-500 hover:bg-gold-600 text-stone-900"
                >
                  첫 추천업체 추가하기
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-stone-600">이미지</th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-stone-600">업체명</th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-stone-600">카테고리</th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-stone-600">연락처</th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-stone-600">상태</th>
                      <th className="text-right py-4 px-6 text-sm font-semibold text-stone-600">관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sponsors.map((sponsor) => (
                      <tr key={sponsor.id} className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="py-4 px-6">
                          <div className="w-20 h-12 bg-stone-200 rounded-lg overflow-hidden">
                            {sponsor.image_url ? (
                              <img
                                src={sponsor.image_url.startsWith("http") ? sponsor.image_url : `${API_URL}${sponsor.image_url}`}
                                alt={sponsor.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-stone-400" />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <p className="font-medium text-stone-800">{sponsor.title}</p>
                          {sponsor.location && (
                            <p className="text-xs text-stone-500 flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {sponsor.location}
                            </p>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <span className="px-2 py-1 bg-gold-100 text-gold-700 text-xs rounded-full">
                            {categories.find(c => c.value === sponsor.category)?.label || sponsor.category}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          {sponsor.contact && (
                            <span className="text-sm text-stone-600 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {sponsor.contact}
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          {sponsor.is_active ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">활성</span>
                          ) : (
                            <span className="px-2 py-1 bg-stone-100 text-stone-500 text-xs rounded-full">비활성</span>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEditSponsor(sponsor)}
                              className="p-2 text-stone-400 hover:text-gold-500 hover:bg-gold-50 rounded-lg transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSponsor(sponsor.id)}
                              className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/80 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8">
            <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-stone-800">
                {editingContent ? "콘텐츠 수정" : "새 콘텐츠 추가"}
              </h2>
              <button
                onClick={resetForm}
                className="p-2 text-stone-400 hover:text-stone-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  제목 *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-500 focus:border-transparent"
                  placeholder="콘텐츠 제목을 입력하세요"
                />
              </div>

              {/* Category & Content Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    카테고리 *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-500 focus:border-transparent"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    콘텐츠 유형 *
                  </label>
                  <select
                    value={formData.content_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, content_type: e.target.value }))}
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-500 focus:border-transparent"
                  >
                    {contentTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  설명 *
                </label>
                <textarea
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-500 focus:border-transparent resize-none"
                  placeholder="콘텐츠에 대한 설명을 입력하세요"
                />
              </div>

              {/* Media Upload */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  미디어 파일 (영상/이미지)
                </label>
                <div className="border-2 border-dashed border-stone-200 rounded-xl p-6 text-center">
                  {formData.media_url ? (
                    <div className="space-y-3">
                      <p className="text-sm text-green-600">파일이 업로드되었습니다</p>
                      <p className="text-xs text-stone-400 break-all">{formData.media_url}</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData(prev => ({ ...prev, media_url: "" }))}
                      >
                        다른 파일 선택
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*,video/*"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e.target.files[0], "media")}
                      />
                      {uploadingMedia ? (
                        <Loader2 className="w-8 h-8 text-burgundy-500 animate-spin mx-auto" />
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                          <p className="text-stone-500">클릭하여 파일 업로드</p>
                          <p className="text-xs text-stone-400 mt-1">영상 또는 이미지</p>
                        </>
                      )}
                    </label>
                  )}
                </div>
                <p className="text-xs text-stone-400 mt-2">또는 URL 직접 입력:</p>
                <input
                  type="text"
                  value={formData.media_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, media_url: e.target.value }))}
                  className="w-full mt-1 px-4 py-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-burgundy-500 focus:border-transparent"
                  placeholder="https://..."
                />
              </div>

              {/* Thumbnail Upload */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  썸네일 이미지
                </label>
                <div className="border-2 border-dashed border-stone-200 rounded-xl p-6 text-center">
                  {formData.thumbnail_url ? (
                    <div className="space-y-3">
                      <img
                        src={formData.thumbnail_url.startsWith("http") ? formData.thumbnail_url : `${API_URL}${formData.thumbnail_url}`}
                        alt="Thumbnail preview"
                        className="w-32 h-20 object-cover rounded-lg mx-auto"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData(prev => ({ ...prev, thumbnail_url: "" }))}
                      >
                        다른 이미지 선택
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e.target.files[0], "thumbnail")}
                      />
                      {uploadingThumbnail ? (
                        <Loader2 className="w-8 h-8 text-burgundy-500 animate-spin mx-auto" />
                      ) : (
                        <>
                          <Image className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                          <p className="text-stone-500">클릭하여 썸네일 업로드</p>
                        </>
                      )}
                    </label>
                  )}
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  태그 (쉼표로 구분)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                  className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-500 focus:border-transparent"
                  placeholder="패션, 스타일링, 봄코디"
                />
              </div>

              {/* Premium Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_premium"
                  checked={formData.is_premium}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_premium: e.target.checked }))}
                  className="w-5 h-5 rounded border-stone-300 text-burgundy-500 focus:ring-burgundy-500"
                />
                <label htmlFor="is_premium" className="flex items-center gap-2 text-stone-700">
                  <Crown className="w-5 h-5 text-gold-500" />
                  프리미엄 콘텐츠 (정회원 전용)
                </label>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  className="flex-1 py-3"
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-3 bg-burgundy-500 hover:bg-burgundy-600"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-5 h-5 mr-2" />
                      {editingContent ? "수정하기" : "저장하기"}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sponsor Add/Edit Form Modal */}
      {showSponsorForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/80 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8">
            <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-stone-800">
                {editingSponsor ? "추천업체 수정" : "새 추천업체 추가"}
              </h2>
              <button
                onClick={resetSponsorForm}
                className="p-2 text-stone-400 hover:text-stone-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSponsorSubmit} className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  업체명 *
                </label>
                <input
                  type="text"
                  required
                  value={sponsorFormData.title}
                  onChange={(e) => setSponsorFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  placeholder="업체명을 입력하세요"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  카테고리 *
                </label>
                <select
                  value={sponsorFormData.category}
                  onChange={(e) => setSponsorFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  설명 *
                </label>
                <textarea
                  required
                  rows={3}
                  value={sponsorFormData.description}
                  onChange={(e) => setSponsorFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent resize-none"
                  placeholder="업체에 대한 간단한 설명을 입력하세요"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  대표 이미지
                </label>
                <div className="border-2 border-dashed border-stone-200 rounded-xl p-6 text-center">
                  {sponsorFormData.image_url ? (
                    <div className="space-y-3">
                      <img
                        src={sponsorFormData.image_url.startsWith("http") ? sponsorFormData.image_url : `${API_URL}${sponsorFormData.image_url}`}
                        alt="Preview"
                        className="w-40 h-24 object-cover rounded-lg mx-auto"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setSponsorFormData(prev => ({ ...prev, image_url: "" }))}
                      >
                        다른 이미지 선택
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleSponsorImageUpload(e.target.files[0])}
                      />
                      {uploadingSponsorImage ? (
                        <Loader2 className="w-8 h-8 text-gold-500 animate-spin mx-auto" />
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                          <p className="text-stone-500">클릭하여 이미지 업로드</p>
                        </>
                      )}
                    </label>
                  )}
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  위치
                </label>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-stone-400" />
                  <input
                    type="text"
                    value={sponsorFormData.location}
                    onChange={(e) => setSponsorFormData(prev => ({ ...prev, location: e.target.value }))}
                    className="flex-1 px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="예: 서울 강남구"
                  />
                </div>
              </div>

              {/* Contact */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  연락처
                </label>
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-stone-400" />
                  <input
                    type="text"
                    value={sponsorFormData.contact}
                    onChange={(e) => setSponsorFormData(prev => ({ ...prev, contact: e.target.value }))}
                    className="flex-1 px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="예: 02-1234-5678"
                  />
                </div>
              </div>

              {/* Link URL */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  웹사이트 URL
                </label>
                <div className="flex items-center gap-2">
                  <ExternalLink className="w-5 h-5 text-stone-400" />
                  <input
                    type="url"
                    value={sponsorFormData.link_url}
                    onChange={(e) => setSponsorFormData(prev => ({ ...prev, link_url: e.target.value }))}
                    className="flex-1 px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={sponsorFormData.is_active}
                  onChange={(e) => setSponsorFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="w-5 h-5 rounded border-stone-300 text-gold-500 focus:ring-gold-500"
                />
                <label htmlFor="is_active" className="flex items-center gap-2 text-stone-700">
                  {sponsorFormData.is_active ? (
                    <ToggleRight className="w-5 h-5 text-green-500" />
                  ) : (
                    <ToggleLeft className="w-5 h-5 text-stone-400" />
                  )}
                  페이지에 표시
                </label>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetSponsorForm}
                  className="flex-1 py-3"
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-3 bg-gold-500 hover:bg-gold-600 text-stone-900"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-5 h-5 mr-2" />
                      {editingSponsor ? "수정하기" : "저장하기"}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
